import React, { FC, PropsWithChildren, useEffect, useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { sharedLib } from "@shared/services/shared.service";
import { useRouteMatch } from "react-router-dom";
import { ADMIN_SIGNALS_PATH } from "common/constants";
import { useSignalsQuery } from "features/signals/common/hooks.tsx";
import {
  SignalsStatus,
  SignalsStatusValueType,
} from "@shared/features/signals/common/types";
import { SignalsPageContext } from "@shared/features/signals/common/SignalsPage.context";
import { useSignalsData } from "@shared/features/signals/common/hooks";
import { useCheckUserMembership } from "utils/hooks/misc";

interface SignalsPageProviderProps {
  isOpen: boolean;
}

const SignalsPageProvider: FC<PropsWithChildren<SignalsPageProviderProps>> = ({
  isOpen,
  children,
}) => {
  const isAdminSignals = Boolean(useRouteMatch(ADMIN_SIGNALS_PATH));
  const { subGroupId, signalsStatus, setSignalStatus } =
    useSignalsQuery(isAdminSignals);
  const [visitedOpenSubGroupId, setVisitedOpenSubGroupId] = useState(
    isOpen ? subGroupId : undefined
  );
  const [visitedClosedSubGroupId, setVisitedClosedSubGroupId] = useState(
    !isOpen ? subGroupId : undefined
  );
  const [tabToCount, setTabToCount] =
    useState<Record<SignalsStatusValueType, number | undefined>>(
      DEFAULT_TAB_COUNTS
    );

  useEffect(() => {
    if (isOpen) {
      setVisitedOpenSubGroupId(subGroupId);
    } else {
      setVisitedClosedSubGroupId(subGroupId);
    }
  }, [subGroupId, isOpen]);

  const visitedGroupId = isOpen
    ? visitedOpenSubGroupId
    : visitedClosedSubGroupId;
  const urlIsOpen = signalsStatus === SignalsStatus.OPEN;
  const isActiveTab = isOpen === urlIsOpen;
  const isVisitedTab = !!subGroupId && visitedGroupId === subGroupId;
  const skipQueries = !isActiveTab && !isVisitedTab;

  const { liveUpdateChannelsQuery } = useCheckUserMembership({
    isOpen,
    isAdminSignals,
  });
  const getGroupsDataStatus =
    sharedLib.sharedApi.settings.useGetGroupsDataQuery(
      isAdminSignals ? skipToken : undefined
    );
  const getAdminSubGroupsStatus =
    sharedLib.sharedApi.admin.useGetAdminSubGroupsQuery(
      isAdminSignals ? undefined : skipToken
    );

  const { signalsData, args, getSignalsDataQuery, filtersSlice } =
    useSignalsData(isOpen, isAdminSignals, skipQueries);

  const isSignalsFetching =
    getSignalsDataQuery.isFetching ||
    getGroupsDataStatus.isFetching ||
    getAdminSubGroupsStatus.isFetching ||
    liveUpdateChannelsQuery.isFetching;

  const isSignalsLoading =
    getSignalsDataQuery.isLoading ||
    getGroupsDataStatus.isLoading ||
    getAdminSubGroupsStatus.isLoading ||
    liveUpdateChannelsQuery.isLoading;

  sharedLib.sharedApi.admin.useGetAdminSubGroupDataQuery(
    isAdminSignals && subGroupId
      ? {
          sub_group_id: subGroupId,
        }
      : skipToken
  );

  const showEmpty =
    !getSignalsDataQuery.isUninitialized &&
    !!signalsData &&
    (!!getGroupsDataStatus.data || isAdminSignals);

  return (
    <SignalsPageContext.Provider
      value={{
        isOpen,
        signalsStatus,
        setSignalStatus,
        subGroupId,
        isAdminSignals,
        tabToCount,
        setTabToCount,
        signalsData,
        args,
        getSignalsDataQuery,
        filtersSlice,
        isSignalsFetching,
        isSignalsLoading,
        showEmpty,
        getGroupsDataStatus,
        getAdminSubGroupsStatus,
      }}
    >
      {children}
    </SignalsPageContext.Provider>
  );
};

export default SignalsPageProvider;

const DEFAULT_TAB_COUNTS: Record<SignalsStatusValueType, number | undefined> = {
  [SignalsStatus.OPEN]: undefined,
  [SignalsStatus.CLOSED]: undefined,
};
